import { AxiosResponse } from "axios";
import { useCallback, useEffect, useState } from "react";

import { useAxiosClient } from "@/hooks";
import {
  GenerateMidtermsSummariesRequest,
  MidtermsSummaryItem,
  UpdateMidtermsSnapshotRequest,
} from "@/types/requests";
import { MidtermsSnapshot } from "@/types/resources";
import {
  ErrorResponse,
  GenerateMidtermsSummariesResponse,
  GetMidtermsSnapshotResponse,
  UpdateMidtermsSnapshotResponse,
} from "@/types/responses";

/** Writes a headline and summary per item from its figures alone; nothing
    is stored server-side. */
const useGenerateSummaries = () => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const client = useAxiosClient("/midterms");

  const generateSummaries = useCallback(
    async (items: MidtermsSummaryItem[]) => {
      setError(null);
      setLoading(true);

      try {
        const response = await client.post<
          GenerateMidtermsSummariesResponse,
          AxiosResponse<GenerateMidtermsSummariesResponse>,
          GenerateMidtermsSummariesRequest
        >("/summaries", { items });
        return response.data.summaries;
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [client]
  );

  return {
    error,
    generateSummaries,
    loading,
  };
};

/**
 * The published snapshot. Accounts without access (403) and a snapshot never
 * published (404) are reported in `unavailable`, not as errors.
 */
const useGetSnapshot = <Samples, Summaries>() => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [snapshot, setSnapshot] = useState<MidtermsSnapshot<
    Samples,
    Summaries
  > | null>(null);
  const [unavailable, setUnavailable] = useState<
    "forbidden" | "missing" | null
  >(null);

  const client = useAxiosClient("/midterms");

  const getSnapshot = useCallback(() => {
    client
      .get<GetMidtermsSnapshotResponse<Samples, Summaries> | ErrorResponse>(
        "/snapshot",
        {
          validateStatus: (status) =>
            (status >= 200 && status < 300) || status === 403 || status === 404,
        }
      )
      .then((response) => {
        if ("snapshot" in response.data) {
          setSnapshot(response.data.snapshot);
          setUnavailable(null);
        } else {
          setUnavailable(response.status === 403 ? "forbidden" : "missing");
        }
        setError(null);
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        setLoading(false);
      });
  }, [client]);

  const refresh = useCallback(() => {
    setLoading(true);
    setError(null);
    getSnapshot();
  }, [getSnapshot]);

  useEffect(() => {
    getSnapshot();
  }, [getSnapshot]);

  return {
    error,
    loading,
    refresh,
    snapshot,
    unavailable,
  };
};

/** Publishes a snapshot, replacing the previous one; resolves to its
    `published_at`, or null when the request failed. */
const useUpdateSnapshot = <Samples, Summaries>() => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const client = useAxiosClient("/midterms");

  const updateSnapshot = useCallback(
    async (data: UpdateMidtermsSnapshotRequest<Samples, Summaries>) => {
      setError(null);
      setLoading(true);

      try {
        const response = await client.put<
          UpdateMidtermsSnapshotResponse,
          AxiosResponse<UpdateMidtermsSnapshotResponse>,
          UpdateMidtermsSnapshotRequest<Samples, Summaries>
        >("/snapshot", data);
        return response.data.published_at;
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [client]
  );

  return {
    error,
    loading,
    updateSnapshot,
  };
};

export { useGenerateSummaries, useGetSnapshot, useUpdateSnapshot };
